// Model navigasi role-based (rfc-006 §4.1). Item difilter PERMISSION:
// yang tak punya izin disembunyikan (bukan disabled). Dipakai bersama oleh
// sidebar desktop (berkelompok) & bottom tabs mobile (5 + "Lainnya").
//
// Tiap item punya `anyOf`: terlihat bila akun punya SALAH SATU permission
// tsb. Permission disetel agar cocok dengan gate server (main.go) supaya
// menu yang tampil benar-benar bisa diakses.

import {
  BarChart3,
  Boxes,
  Contact,
  HardDrive,
  History,
  Package,
  Settings,
  ShoppingCart,
  TrendingDown,
  Truck,
  Wallet,
  type LucideIcon,
} from "lucide-react";

export type NavItem = {
  key: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /** Terlihat bila `can(p)` true untuk salah satu p (kosong = selalu). */
  anyOf: string[];
};

export type NavGroup = {
  label: string;
  items: NavItem[];
};

export const NAV: NavGroup[] = [
  {
    label: "Operasional",
    items: [
      { key: "cashier", label: "Kasir", path: "/cashier", icon: ShoppingCart, anyOf: ["sale.create"] },
      { key: "history", label: "Riwayat", path: "/history", icon: History, anyOf: ["report.sales.view"] },
      { key: "cash", label: "Kas & Shift", path: "/cash", icon: Wallet, anyOf: ["cash.movement"] },
    ],
  },
  {
    label: "Katalog & Stok",
    items: [
      { key: "products", label: "Produk", path: "/products", icon: Package, anyOf: ["product.manage"] },
      { key: "stock", label: "Stok", path: "/stock", icon: Boxes, anyOf: ["lot.manage"] },
      { key: "restock", label: "Restock", path: "/restock", icon: TrendingDown, anyOf: ["lot.manage"] },
      { key: "suppliers", label: "Supplier", path: "/suppliers", icon: Truck, anyOf: ["supplier.manage"] },
    ],
  },
  {
    label: "Bisnis",
    items: [
      { key: "reports", label: "Laporan", path: "/reports", icon: BarChart3, anyOf: ["report.sales.view"] },
      { key: "customers", label: "Pelanggan", path: "/customers", icon: Contact, anyOf: ["customer.manage"] },
    ],
  },
  {
    label: "Sistem",
    items: [
      {
        key: "settings",
        label: "Pengaturan",
        path: "/settings",
        icon: Settings,
        anyOf: [
          "settings.edit",
          "discount.manage",
          "settings.users.manage",
          "settings.cipherKey.edit",
          // Manajer Cabang masuk Pengaturan untuk tab Cabang (harga/transfer).
          "branch.manage",
          "branch.price.manage",
          "stock.transfer",
        ],
      },
      // Manajemen file S3 — hanya developer (storage.manage; Pemilik tidak).
      { key: "files", label: "File (S3)", path: "/files", icon: HardDrive, anyOf: ["storage.manage"] },
    ],
  },
];

type Can = (permission: string) => boolean;

function itemVisible(item: NavItem, can: Can): boolean {
  return item.anyOf.length === 0 || item.anyOf.some((p) => can(p));
}

/** Grup yang masih punya minimal satu item terlihat (untuk sidebar). */
export function visibleGroups(can: Can): NavGroup[] {
  return NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => itemVisible(item, can)),
  })).filter((group) => group.items.length > 0);
}

/** Semua item terlihat, dirata (untuk bottom tabs & default route). */
export function visibleItems(can: Can): NavItem[] {
  return NAV.flatMap((group) => group.items).filter((item) => itemVisible(item, can));
}

/** Path tujuan default = item terlihat pertama (fallback /cashier). */
export function defaultPath(can: Can): string {
  return visibleItems(can)[0]?.path ?? "/cashier";
}

/** Pemecahan bottom tabs: maks 4 tab langsung + sisanya ke sheet "Lainnya". */
export function splitBottomTabs(can: Can): { tabs: NavItem[]; more: NavItem[] } {
  const items = visibleItems(can);
  if (items.length <= 5) return { tabs: items, more: [] };
  return { tabs: items.slice(0, 4), more: items.slice(4) };
}

export function findItemByPath(path: string): NavItem | undefined {
  return NAV.flatMap((group) => group.items).find((item) => path.startsWith(item.path));
}
